/**
 * Plain (non-React) resumable upload transport: reserve → stream bytes
 * direct-to-GCS (UpChunk in prod, the backend local-PUT shim in dev) → finalize.
 *
 * Lives outside React so the global upload manager store (which is not a
 * component) can drive uploads directly — every resumable upload surface
 * (Import, Add attachment, …) enqueues through the store, which calls this.
 */

import type { AnalyticsEventName } from '@bower/shared/constants/tracking-plan.generated';
import {
  CAPTURE_SURFACE_AGENT_CHAT,
  CAPTURE_SURFACE_ONBOARDING_IMPORT,
} from '@bower/shared/constants/capture';
import { captureSurfaceForClient } from '@bower/shared/constants/client';
import { isVideoFile } from '@bower/shared/utils/fileTypeDetection';
import { PROCESSING_STATUS } from '@bower/shared/constants/artifact-status';
import type {
  CaptureMethod,
  UploadFinalizeResponse,
  UploadIntent,
  UploadReserveRequest,
} from '@bower/shared';

import { ANALYTICS_EVENTS } from '../../constants/analytics';
import { finalizeUpload, reserveUpload, uploadLocalBytes } from '../api/uploads.api';
import { apiService } from '../api.service';
import { safeAnalytics } from '../../utils/analytics';
import { beginUpload, endUpload } from '../../utils/uploadActivity';
import { currentBowerClient } from '../../utils/bowerClient';
// Chunk sizing lives in a leaf module so the contract test can validate it
// against UpChunk without importing this file's service graph. See chunkConfig.
import { CHUNK_SIZE_KB, DYNAMIC_CHUNK_MIN_KB, DYNAMIC_CHUNK_MAX_KB } from './chunkConfig';

function isVoiceRecording(args: PerformUploadArgs): boolean {
  return args.voiceRecordingDurationMs !== undefined;
}

/** The resumable transport backs ALL attachment types now. Video keeps the
 * VIDEO_UPLOAD_* events (existing transcode-cost dashboards); everything else
 * emits ATTACHMENT_UPLOAD_* so non-video traffic doesn't pollute video funnels. */
function uploadEvent(
  file: File,
  args: PerformUploadArgs,
  kind: 'started' | 'completed' | 'failed',
): AnalyticsEventName {
  if (isVoiceRecording(args)) {
    if (kind === 'started') return ANALYTICS_EVENTS.VOICE_RECORDING_UPLOAD_STARTED;
    if (kind === 'completed') return ANALYTICS_EVENTS.VOICE_RECORDING_UPLOAD_COMPLETED;
    return ANALYTICS_EVENTS.VOICE_RECORDING_UPLOAD_FAILED;
  }
  const v = isVideoFile(file.name, file.type);
  if (kind === 'started')
    return v ? ANALYTICS_EVENTS.VIDEO_UPLOAD_STARTED : ANALYTICS_EVENTS.ATTACHMENT_UPLOAD_STARTED;
  if (kind === 'completed')
    return v
      ? ANALYTICS_EVENTS.VIDEO_UPLOAD_COMPLETED
      : ANALYTICS_EVENTS.ATTACHMENT_UPLOAD_COMPLETED;
  return v ? ANALYTICS_EVENTS.VIDEO_UPLOAD_FAILED : ANALYTICS_EVENTS.ATTACHMENT_UPLOAD_FAILED;
}

export interface PerformUploadArgs {
  readonly workspaceId: string;
  readonly parentEntityType: string;
  readonly parentEntityId: string;
  /** User-typed title (single-file only). Blank titles follow
   * FILE_PROCESSING_CONFIG.titlePolicy: filename stores the original filename;
   * ai-generated stores null pending generation. */
  readonly title?: string;
  /** Import intent — note-convertible files spawn a note. Default false = attach. */
  readonly importAsNotes?: boolean;
  readonly captureMethod?: CaptureMethod;
  /** Lifecycle intent only — the device comes from `x-bower-client`. Rides
   * through to the reserve body so an onboarding import keeps its surface,
   * and a chat composer upload keeps `agent_chat` (the surface the agent's
   * re-post used to get from `x-bower-client: agent`). Both are
   * body-declarable; the server-assigned ones are refused at reserve. */
  readonly captureSurface?:
    | typeof CAPTURE_SURFACE_ONBOARDING_IMPORT
    | typeof CAPTURE_SURFACE_AGENT_CHAT;
  readonly multipleSpeakers?: boolean;
  /** Server-owned transcription cutover for audio recorded into a note: the
   * backend note handler queues transcribe-note-audio only when this rode the
   * reserve. New clients set it and do NOT transcribe/append client-side. */
  readonly serverTranscription?: boolean;
  /** Server-owned capture for a file added to a note: 'ocr' asks the backend
   * to read a photo's text into the note in the background (note-ocr job). */
  readonly noteCapture?: 'transcript' | 'ocr';
  /** Marks a completed browser recording so its transfer receives voice-specific telemetry. */
  readonly voiceRecordingDurationMs?: number;
  /** Replays only server finalization after an ambiguous response. The bytes
   * already landed, so reserving and transferring again would duplicate data. */
  readonly resumeFinalizeUploadId?: string;
  /** Replays finalize once, inside this same upload, when the first answer was
   * ambiguous (a non-terminal finalize failure: the bytes landed, so only
   * finalize is replayed) and the signal was not aborted. A second failure
   * throws that second error. Passed by the chat composer and the Create
   * protocol, Create spreadsheet, photo review and OCR doors. Not by
   * `useVoiceRecording`, which keeps its own resume policy through
   * `resumeFinalizeUploadId`. */
  readonly resumeAmbiguousFinalizeOnce?: true;
  /** Note target for the Import flow ('protocol' for the Protocol Library);
   * only consulted server-side when importAsNotes. */
  readonly targetArtifactType?: string;
  /** The richer intent vocabulary (see `UploadIntent`). Supersedes
   * `importAsNotes` when present; absent preserves the boolean's meaning. */
  readonly uploadIntent?: UploadIntent;
  /** The Create dialog's Import: the server reads it for the import event's
   * `source`. Absent everywhere else, which the server reports as 'upload'. */
  readonly importSource?: UploadReserveRequest['importSource'];
  /** Give the new artifact this one's audience from birth (privacy row and
   * grants). The spreadsheet rescue copy sends its original. */
  readonly privacySourceArtifactId?: string;
}

export interface PerformUploadResult {
  readonly artifactId: string;
  /** 'ready' for non-video; 'transcoding' for video (worker runs next). */
  readonly processingStatus: UploadFinalizeResponse['processingStatus'];
  /** What the upload became, from finalize: 'spreadsheet' for a CSV/XLSX import. */
  readonly artifactType?: string;
  /** Bounded code from reserve: why a CSV/XLSX stayed a file. Never a file name. */
  readonly intakeNote?: string;
}

interface UpchunkLike {
  on(event: 'progress', cb: (e: { detail: number }) => void): void;
  on(event: 'success', cb: () => void): void;
  on(
    event: 'attemptFailure',
    cb: (e: { detail: { attemptsLeft?: number; response?: { statusCode?: number } } }) => void,
  ): void;
  on(
    event: 'error',
    cb: (e: {
      detail: { message?: string; attempts?: number; response?: { statusCode?: number } };
    }) => void,
  ): void;
  destroy(): void;
}

type UploadStage = 'reserve' | 'transfer' | 'finalize';

export class ResumableUploadAttemptError extends Error {
  readonly code: string;
  readonly stage: UploadStage;
  readonly retryFinalizeUploadId?: string;
  /** The reserve's bounded intake code, on a finalize failure after a reserve
   * that gave one. A resumed finalize has no reserve response, so the
   * opt-in resume (`resumeAmbiguousFinalizeOnce`) re-attaches this to the result. Absent otherwise: `declare`
   * emits no class field, so an error without a note has no such key. */
  declare readonly intakeNote?: string;

  constructor(
    message: string,
    code: string,
    stage: UploadStage,
    retryFinalizeUploadId?: string,
    intakeNote?: string,
  ) {
    super(message);
    this.name = 'ResumableUploadAttemptError';
    this.code = code;
    this.stage = stage;
    if (retryFinalizeUploadId) this.retryFinalizeUploadId = retryFinalizeUploadId;
    if (intakeNote) this.intakeNote = intakeNote;
  }
}

function track(event: AnalyticsEventName, props: Record<string, unknown>): void {
  safeAnalytics.track(event, props as Record<string, unknown>);
}

/**
 * `errorCategory` on a failure; `transferMs` on the terminal 'completed' event
 * (without which how long the bytes took is unobservable — a chunk-tuning change
 * can't be told apart from a network blip in prod). `retryCount` applies to
 * both. One object keeps the tracker's arity in check.
 */
interface UploadEventDetail {
  errorCategory?: string;
  retryCount?: number;
  transferMs?: number;
}

function trackUpload(
  file: File,
  args: PerformUploadArgs,
  kind: 'started' | 'completed' | 'failed',
  stage: UploadStage,
  detail?: UploadEventDetail,
): void {
  const throughputKbps =
    detail?.transferMs && detail.transferMs > 0
      ? Math.round((file.size * 8) / detail.transferMs) // bits/ms === kbit/s
      : undefined;
  track(uploadEvent(file, args, kind), {
    workspace_id: args.workspaceId,
    size_bytes: file.size,
    stage,
    ...(detail?.errorCategory === undefined ? {} : { error_category: detail.errorCategory }),
    ...(detail?.transferMs === undefined ? {} : { transfer_ms: detail.transferMs }),
    ...(throughputKbps === undefined ? {} : { throughput_kbps: throughputKbps }),
    ...(detail?.retryCount === undefined ? {} : { retry_count: detail.retryCount }),
    ...(isVoiceRecording(args)
      ? {
          // Still the DEVICE, and still spelled `desktop`/`mobile`: this is a
          // live PostHog property, not the artifact column. Derived from the
          // one client rule via the shared map rather than from
          // `args.captureSurface`, which no longer carries a device — keeping
          // the payload identical while the body stops guessing.
          capture_surface: captureSurfaceForClient(currentBowerClient()),
          multiple_speakers: args.multipleSpeakers ?? false,
          duration_ms: args.voiceRecordingDurationMs,
        }
      : {}),
  });
}

function safeErrorCategory(value: unknown, fallback: string): string {
  return typeof value === 'string' && /^[A-Z0-9_]{1,80}$/.test(value) ? value : fallback;
}

const TERMINAL_FINALIZE_CODES = new Set([
  'UPLOAD_NOT_FOUND',
  'OBJECT_MISSING',
  'OVER_SIZE_CAP',
  'VIEWER_FORBIDDEN',
]);

/**
 * Upload one file. Resolves with the finalized artifact id + processing status.
 * Rejects on reserve/upload/finalize failure. `signal` aborts an in-flight GCS
 * upload (the tray's cancel). Registers the upload with the beforeunload guard
 * for its whole lifetime.
 */
export async function performResumableUpload(
  file: File,
  args: PerformUploadArgs,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal,
): Promise<PerformUploadResult> {
  beginUpload();
  try {
    return await runUploadResumingFinalize(file, args, onProgress, signal);
  } finally {
    endUpload();
  }
}

/** `runUpload`, replaying an ambiguous finalize once when the caller opted in
 * (see `resumeAmbiguousFinalizeOnce`). The replay is the `resumeFinalizeUploadId`
 * path, so it emits its own 'started' event at stage 'finalize'. */
async function runUploadResumingFinalize(
  file: File,
  args: PerformUploadArgs,
  onProgress: ((pct: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<PerformUploadResult> {
  try {
    return await runUpload(file, args, onProgress, signal);
  } catch (error) {
    if (
      !args.resumeAmbiguousFinalizeOnce ||
      // Already a replay: resuming it again would be a second resume.
      args.resumeFinalizeUploadId ||
      signal?.aborted ||
      !(error instanceof ResumableUploadAttemptError) ||
      error.stage !== 'finalize' ||
      !error.retryFinalizeUploadId
    ) {
      throw error;
    }
    const resumed = { ...args, resumeFinalizeUploadId: error.retryFinalizeUploadId };
    const result = await runUpload(file, resumed, undefined, signal);
    // The resumed finalize has no reserve response; the first error kept the note.
    return error.intakeNote ? { ...result, intakeNote: error.intakeNote } : result;
  }
}

/** Pure construction of the reserve request body, pulled out of `runUpload`
 * so that function's branching stays under the complexity ceiling. No
 * behavior change: same keys, same conditional spreads. */
function buildReserveBody(file: File, args: PerformUploadArgs): UploadReserveRequest {
  return {
    parentEntityType: args.parentEntityType,
    parentEntityId: args.parentEntityId,
    fileName: file.name,
    mimeType: file.type || 'application/octet-stream',
    declaredSize: file.size,
    ...(args.title ? { title: args.title } : {}),
    ...(args.importAsNotes ? { importAsNotes: true } : {}),
    ...(args.captureMethod ? { captureMethod: args.captureMethod } : {}),
    ...(args.captureSurface ? { captureSurface: args.captureSurface } : {}),
    ...(args.multipleSpeakers ? { multipleSpeakers: true } : {}),
    ...(args.serverTranscription ? { serverTranscription: true } : {}),
    ...(args.noteCapture ? { noteCapture: args.noteCapture } : {}),
    ...(args.targetArtifactType ? { targetArtifactType: args.targetArtifactType } : {}),
    ...(args.uploadIntent ? { uploadIntent: args.uploadIntent } : {}),
    ...(args.importSource ? { importSource: args.importSource } : {}),
    ...(args.privacySourceArtifactId
      ? { privacySourceArtifactId: args.privacySourceArtifactId }
      : {}),
  };
}

async function runUpload(
  file: File,
  args: PerformUploadArgs,
  onProgress: ((pct: number) => void) | undefined,
  signal: AbortSignal | undefined,
): Promise<PerformUploadResult> {
  if (args.resumeFinalizeUploadId) {
    trackUpload(file, args, 'started', 'finalize');
    return finalize(args.resumeFinalizeUploadId, file, args);
  }

  trackUpload(file, args, 'started', 'reserve');

  let reserved;
  try {
    reserved = await reserveUpload(apiService, args.workspaceId, buildReserveBody(file, args));
  } catch (error) {
    const code = safeErrorCategory((error as { code?: unknown } | null)?.code, 'reserve_failed');
    trackUpload(file, args, 'failed', 'reserve', { errorCategory: code });
    throw error;
  }
  if (!reserved.success || !reserved.data) {
    const code = safeErrorCategory(reserved.error?.code, 'reserve_failed');
    trackUpload(file, args, 'failed', 'reserve', { errorCategory: code });
    throw new ResumableUploadAttemptError(
      reserved.error?.message ?? 'Could not start the upload',
      code,
      'reserve',
    );
  }
  const { uploadId, sessionUri, intakeNote } = reserved.data;
  const report = (pct: number) => onProgress?.(pct);
  // Only reserve knows why a file stayed a file. A resumed finalize has no
  // reserve response, so it reports no note rather than a guessed one.
  const withNote = (result: PerformUploadResult): PerformUploadResult =>
    intakeNote ? { ...result, intakeNote } : result;

  // Local-storage dev: stream bytes to the backend shim, then finalize.
  if (sessionUri.startsWith('local:')) {
    let put;
    try {
      put = await uploadLocalBytes(apiService, args.workspaceId, uploadId, file, {
        onProgress: report,
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      const code = safeErrorCategory((error as { code?: unknown } | null)?.code, 'transfer_failed');
      trackUpload(file, args, 'failed', 'transfer', { errorCategory: code });
      throw error;
    }
    if (!put.success) {
      const code = safeErrorCategory(put.error?.code, 'transfer_failed');
      trackUpload(file, args, 'failed', 'transfer', { errorCategory: code });
      throw new ResumableUploadAttemptError(
        put.error?.message ?? 'The upload failed',
        code,
        'transfer',
      );
    }
    return withNote(await finalize(uploadId, file, args, undefined, intakeNote));
  }

  // Prod: UpChunk streams the bytes DIRECT to GCS, then finalize.
  const { createUpload } = await import('@mux/upchunk');
  return new Promise<PerformUploadResult>((resolve, reject) => {
    const transferStart = Date.now();
    const uploader = createUpload({
      endpoint: sessionUri,
      file,
      chunkSize: CHUNK_SIZE_KB,
      dynamicChunkSize: true,
      minChunkSize: DYNAMIC_CHUNK_MIN_KB,
      maxChunkSize: DYNAMIC_CHUNK_MAX_KB,
      attempts: 5,
      delayBeforeAttempt: 1,
    }) as unknown as UpchunkLike;
    let settled = false;
    let retryCount = 0;

    const cleanup = () => {
      signal?.removeEventListener('abort', onAbort);
      uploader.destroy();
    };
    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    };
    const onAbort = () => {
      rejectOnce(new ResumableUploadAttemptError('Upload canceled', 'canceled', 'transfer'));
    };

    // UpChunk's abort cancels the XHR via `onabort` — it fires NEITHER `success`
    // NOR `error`, so without rejecting here the outer Promise would hang forever
    // (and `performResumableUpload`'s `finally { endUpload() }` would never run,
    // leaving the beforeunload guard armed for the rest of the session). Reject
    // explicitly so the cancel settles the chain.
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) onAbort();

    uploader.on('progress', (e) => {
      if (!settled) report(Math.round(e.detail));
    });
    uploader.on('attemptFailure', () => {
      if (!settled) retryCount += 1;
    });
    uploader.on('error', (e) => {
      if (settled) return;
      trackUpload(file, args, 'failed', 'transfer', {
        errorCategory: 'transfer_exhausted',
        retryCount,
      });
      rejectOnce(
        new ResumableUploadAttemptError(
          e.detail.message ?? 'The upload failed',
          'transfer_exhausted',
          'transfer',
        ),
      );
    });
    uploader.on('success', () => {
      if (settled) return;
      settled = true;
      cleanup();
      finalize(
        uploadId,
        file,
        args,
        { transferMs: Date.now() - transferStart, retryCount },
        intakeNote,
      )
        .then((result) => resolve(withNote(result)))
        .catch(reject); // finalize threads the metric into the 'completed' event
    });
  });
}

async function finalize(
  uploadId: string,
  file: File,
  args: PerformUploadArgs,
  transfer?: { transferMs: number; retryCount: number },
  intakeNote?: string,
): Promise<PerformUploadResult> {
  const finalized = await finalizeUpload(apiService, args.workspaceId, uploadId);
  if (!finalized.success || !finalized.data) {
    const code = safeErrorCategory(finalized.error?.code, 'finalize_failed');
    trackUpload(file, args, 'failed', 'finalize', { errorCategory: code });
    throw new ResumableUploadAttemptError(
      finalized.error?.message ?? 'Could not finalize the upload',
      code,
      'finalize',
      TERMINAL_FINALIZE_CODES.has(code) ? undefined : uploadId,
      intakeNote,
    );
  }
  const processingStatus = finalized.data.processingStatus;
  if (
    processingStatus !== PROCESSING_STATUS.READY &&
    processingStatus !== PROCESSING_STATUS.TRANSCODING
  ) {
    const code = 'unexpected_finalize_status';
    trackUpload(file, args, 'failed', 'finalize', { errorCategory: code });
    throw new ResumableUploadAttemptError(
      'Upload finalized with an unexpected processing status',
      code,
      'finalize',
      uploadId,
      intakeNote,
    );
  }
  trackUpload(file, args, 'completed', 'finalize', transfer);
  const { artifactType } = finalized.data;
  return {
    artifactId: finalized.data.artifactId,
    processingStatus,
    ...(artifactType ? { artifactType } : {}),
  };
}
